export default {
  pages: {
    rafConfiguration: {
      title: "RAF (Reasonable Adjustments Flag) Configuration",
      endpointLabel: "Endpoint (*)",
      apiKeyLabel: "API Key (*)",
      jwtKidLabel: "JWT KID (*)",
      privateKeyLabel: "Private key (not shown) (*)",
      submit: "Submit",
      updateSuccess: "RAF configuration updated",
      updateError: "Failed to update RAF configuration",
    },
    guestUserLoginPage: {
      emailSent: "We’ve sent a 6-character guest user access code to ",
      expiryNotice:
        "The guest user access code expires shortly, so please enter it soon.",
      enterCodeLabel: "Enter guest user access code",
      resendCode: "RESEND GUEST USER ACCESS CODE",
      codeSent: "CODE SENT",
      invalidLink:
        "This link is invalid. Please contact support@thalamos.co.uk if you believe you should have access to a Thalamos Guest Login",
      unknownError: "Unknown Error",
    },
    shareCodePage: {
      title:
        "To access any forms shared with you by the Police, please enter the incident code they provide.",
      incidentCodeLabel: "Enter incident code:",
      confirmPrompt:
        "Please confirm your name and email to view the shared form.",
      accessingCodeLinkLabel: "Problems accessing guest user code?",
    },
    mcaAssessment: {
      forms: "Forms",
      failedCreateForm: "Failed to start the form. Please try again.",
      scrutinyBanner:
        "Mark administrative scrutiny for this assessment complete",
      prompt: {
        "mca-bid": {
          heading: "Consider doing a Best Interest Decision",
          body: "This Functional and Diagnostic Mental Capacity Assessment is complete. You may want to start a linked Best Interest Decision for this patient.",
          button: "Start BID",
        },
        "mca-fdmca": {
          heading:
            "Consider doing a Functional and Diagnostic Mental Capacity Assessment",
          body: "This Best Interest Decision is complete. You may want to start a linked Functional and Diagnostic Mental Capacity Assessment for this patient.",
          button: "Start FDMCA",
        },
      },
      considerFdmcaDialog: {
        heading:
          "Consider a Functional and Diagnostic Mental Capacity Assessment",
        body: "You have initiated a Best Interest Decision. Consider doing a FDMCA first to establish whether the individual has capacity.",
        continueToBid: "Continue to BID",
        startFdmca: "Start FDMCA",
      },
    },
    soadAssessment: {
      heading: "SOAD Assessment",
      notes: {
        heading: "SOAD Notes",
        subHeading:
          "Use this space to keep any general notes during your assessment. These notes will be submitted to the CQC but not to the provider.",
      },
      soadReport: "SOAD Report",
      certificates: {
        heading: "Statutory Certificates",
        prompt: "Select the Statutory Certificates for the Assessment",
        subText:
          'You must select at least one certificate from the list above. Statutory Certificates may not be selected in combination with "No Certificate".',
      },
      supersededCertificates: "Superseded Certificates",
      restrictedDisclosure: {
        chip: "Restricted disclosure",
        form: "Restricted disclosure recorded on this form.",
        formDetail:
          "The SOAD judged that disclosure of the reasons would be likely to cause serious harm to the patient or others. Do not share this document with the patient.",
        version:
          "Restricted disclosure recorded on this version (version number: {{version}}).",
        versionDetail:
          "The SOAD judged that disclosure of the reasons on this version would be likely to cause serious harm to the patient or others. Do not share this document with the patient.",
      },
      selectCertificate: "Select Certificate",
      alreadyAmendedMessage:
        "This assessment has been previously amended and resubmitted. All associated forms from the assessment history are available on this page, including those that have been superseded or withdrawn. Please ensure you are viewing the correct version for your needs.",
      amendmentMessage: "To amend this assessment, please click here",
      startAmendment: "Start amendment",
      amendmentInProgressMessage: "Amendment in progress on this assessment",
      soadMakingAmendment:
        "This assessment has been reopened by the SOAD to make amendments. This assessment may be superseded by an updated version following the amendment.",
      soadMakingAmendmentModalTitle:
        "This assessment is currently being amended",
      soadMakingAmendmentModalBody:
        "This assessment has been reopened by the SOAD to make amendments. The original version is still accessible; however, please note that it may soon be superseded by an updated version following the amendment.",
      cancelAmendment: "Cancel amendment",
      certificateTitles: {
        t2: "Form T2: Section 58(3)(a) - certificate of consent to treatment",
        t3: "Form T3: Section 58(3)(b) - certificate of second opinion",
        t4: "Form T4: Section 58A(3) - certificate of consent to treatment (patients at least 18 years old)",
        t5: "Form T5: Section 58A(4) - certificate of consent to treatment and second opinion (patients under 18)",
        t6: "Form T6: Section 58A(5) - certificate of second opinion (patients who are not capable of understanding the nature, purpose and likely effects of the treatment)",
        cto11:
          "Form CTO11: Section 64C(4) - certificate of appropriateness of treatment to be given to community patient (Part 4A certificate)",
        nocert: "No Certificate",
      },
      shortCertificateTitles: {
        t2: "Form T2: Section 58(3)(a)",
        t3: "Form T3: Section 58(3)(b)",
        t4: "Form T4: Section 58A(3)",
        t5: "Form T5: Section 58A(4)",
        t6: "Form T6: Section 58A(5)",
        cto11: "Form CTO11: Section 64C(4)",
        nocert: "No Certificate",
      },
      srf: {
        assessmentDetails: "Assessment Details",
        patientDetails: "Patient Details",
        diagnosisAndHistory: "Diagnosis and History",
        currentAndProposedTreatment: "Current and Proposed Treatment",
        consulteeDetails: "Consultee Details",
        soadAuthorisation: "SOAD Authorisation",
      },
      deleteCertificateTitle: "Delete Certificate",
      deleteCertificateMessage:
        "This will delete {{ certificate }} and all the information you’ve entered into it. You can add the certificate again later, but any previous information will be lost and cannot be recovered.",
      deleteCertificateError: "Failed to delete certificate",
      addCertificateError: "Failed to add certificate",
      add: "Add",
      quickCreate: {
        title: "Quick-create SOAD assessment",
        subtitle:
          "Creates a patient and an assessment with every report section and one statutory certificate completed, ready to submit for review and sign.",
        nhsNumberLabel: "NHS number (optional)",
        nhsNumberSubtext:
          "Leave blank to create a local patient with no NHS number.",
        nhsNumberInvalid:
          "Enter a valid 10-digit NHS number, or leave blank to create a local patient",
        creating: "Creating...",
        failed: "Failed",
        created: "Created SOAD assessment {{ requestId }}",
        steps: {
          creatingPatient: "Creating patient",
          findingPatientInPds: "Finding patient in PDS",
          creatingAssessment: "Creating assessment {{ requestId }}",
          completingReportSections: "Completing report sections",
          addingCertificate: "Adding statutory certificate",
          completingCertificate: "Completing statutory certificate",
        },
      },
      nhsNumberSearch: {
        continueInstruction:
          " If this is the correct patient, click 'Select Patient'.",
        alternativeInstruction: " or click 'Input Details Manually'.",
        nhsNumberConstraint:
          "You are limited to using the following NHS Number: {{nhsNumber}}",
      },
      patientCreate: {
        title: "Patient Details",
        info: "Please enter the patient’s details to the best of your current knowledge. You will have the opportunity to amend these details before you submit your assessment.",
      },
      gpPractice: {
        odsCode: "ODS Code",
        practiceName: "GP Practice Name",
        practiceAddress: "GP Practice Address",
      },
      amendConfirmationDialog: {
        title: "Amend Assessment",
        body: "You are about to open an assessment which has been previously submitted in order to make amendments. The amended assessment will need to be submitted as a new submission.\nThe CQC and Providers who received the original submission will be notified if you choose to continue to the amend.",
        dropdown: {
          label: "Select Reason",
          options: {
            patientNameMisspelt: "Patient name misspelt",
            consulteeNameMisspelt: "Consultee name misspelt",
            incorrectConsulteeNamed: "Incorrect consultee named",
            incorrectStatutoryFormUsed: "Incorrect statutory form used",
            incorrectPatientAddress: "Incorrect patient address",
            missingMedication: "Missing medication",
            missingQuantityOfMedication: "Missing quantity of medication",
            missingRouteOfMedicine: "Missing route of medicine",
            medicationQuery: "Medication query",
            wrongBnfCodeLimit: "Wrong BNF code/limit",
            other: "Other",
          },
        },
      },
      uploadToRio: "Upload to Rio",
      uploadAllToRio: "Upload all to Rio",
      srfNotFoundError: "Soad Report From not found",
    },
    editMHAStatus: {
      trustAutoSelected:
        "This is the Trust/Private Provider associated with the Hospital you have selected. The additional name will not appear on any forms.",
      currentMHAStatus: "Current MHA status",
      updateMHAStatus: "Update MHA Status",
      successMessage: "Patient MHA status updated",
      errorMessage: "Failed to update patient MHA status",
      unknown: "Unknown",
      section5_4: "Section 5(4)",
      section5_2: "Section 5(2)",
      section4: "Section 4",
      section2: "Section 2",
      section3: "Section 3",
      cto: "CTO",
      notDetained: "Not detained",
      startDateTime: "Start date and time of detention",
      endDateTime: "End date and time of detention",
      section136Extended: "Has the Section 136 been extended?",
      section136ExtensionHours:
        "By how many hours has the detention been extended?",
      originalSection136EndDate: "Original Section 136 end date",
      currentSection136EndDate: "Current Section 136 end date",
      section136ExtendedError:
        "Please confirm if the Section 136 has been extended.",
      section136ExtensionHoursError:
        "Please confirm how long the Section 136 has been extended. Maximum 12 hours",
      startDate: "Start date of detention",
      endDate: "End date of detention",
      hospital: "Hospital",
      updateStatusBtn: "Update status",
      discardBtn: "Back to patient",
      hasRenewalCTO: "Has the original CTO period been extended?",
      hasRenewalSection3: "Has the original Section 3 period been renewed?",
      renewals: "Renewals",
      extensions: "Extensions",
      originalCTOStartDate: "Original CTO start date",
      originalCTOExpiryDate: "Original CTO end date",
      originalSection3StartDate: "Original Section 3 start date",
      originalSection3ExpiryDate: "Original Section 3 end date",
      ctoStartDate: "Current CTO start date",
      ctoExpiryDate: "Current CTO end date",
      section3StartDate: "Current Section 3 start date",
      section3ExpiryDate: "Current Section 3 end date",
      notDetainedReason: "Not detained reason",
      dischargedSection23: "Discharged Section 23",
      dischargedHoldingPowers: "Discharged from holding powers",
      dischargedSection136: "Discharged from Section 136",
      dischargedTribunal: "Discharged by Tribunal/Nearest Relative",
      dateTimeDischarge: "Date and time of discharge",
      previousHospital: "Previous Hospital",
      previousPlaceOfSafety: "Previous Place of Safety",
      placeOfSafety: "Place of Safety",
      placeOfSafetyPopulatedFromForm:
        "Place of Safety populated from Section 136 form",
      hospitalToolTip: "Responsible hospital for the patient’s care",
      scrutinyWarningMessage:
        "Please review information and make sure it is accurate and reflecting the care for this patient before marking scrutiny for this assessment as complete.",
      unknownIncomplete: "Unknown (Incomplete)",
      unknownExpired: "Unknown (Expired)",
      continue: "continue",
      confirmAction:
        "You are about to update MHA Status for this patient. Please make sure you have entered the correct information.",
      success: "Success",
      successModalBody: "MHA Status details have successfully been updated",
      scrutinyConfirmModalAdmission:
        "You are about to mark administrative scrutiny for this assessment as complete. Please note that you cannot add a file note or send a section 15 amend request for this assessment once you confirm. The MHA Status can still be edited once you confirm.",
      scrutinyConfirmModalStandalone:
        "You are about to mark administrative scrutiny for this assessment as complete. Please note that you cannot add a file note to this form once you confirm. The MHA Status can still be edited once you confirm.",
      scrutinyConfirmModalSrf:
        "You are about to mark administrative scrutiny for this assessment as complete. Please note that the SOAD will not be able to amend and resubmit this assessment once you confirm. The MHA Status can still be edited once you confirm.",
      editMHAStatus: "Edit MHA Status",
      other: "Other",
      pendingCTO:
        "The start date is set in the future. Please ensure the patient is currently on a Section 3. The MHA Status of this patient will update to Section 3 (CTO pending) and automatically update to CTO when the latest start date is met.",
      renewalPendingCTO:
        "The start date is set in the future. The MHA Status of this patient will update to CTO (Extension pending) and automatically revert back to CTO when the latest start date is met.",
      renewalPendingSection3:
        "The start date is set in the future. The MHA Status of this patient will update to Section 3 (Renewal pending) and automatically revert back to Section 3 when the latest start date is met.",
    },
    common: {
      signature: {
        label: "Signature",
        checkboxLabel: "Use my existing signature",
        confirmButtonLabel: "I AM HAPPY WITH MY SIGNATURE",
        clearButtonLabel: "CLEAR",
        drawSignatureLabel: "Click to draw",
        uploadLabelErrorImg: "Only {{ validMimeTypes }} files are allowed",
        uploadLabelErrorFileSize1MB: "File size must be less than 1MB",
        uploadLabelErrorFileSize5MB: "File size must be less than 5MB",
      },
    },
    contextSelector: {
      subtitleText: "Team Selector",
      saveAsDefaultTeam: "Save as default team",
      typeaheadLabel: "Team",
      typeaheadPlaceholder: "Start typing to search for role and team",
      logOut: "Log out",
      continue: "Continue",
      welcome: "Welcome",
      chooseContext: "Please choose who you are working for today",
      setupAccount:
        "In order to proceed you need to join a team/organisation or set up your own personal organisation",
    },
    newSoadAssessment: {
      title: "New SOAD Assessment",
      assessment: "Assessment",
      assessmentBannerInfo:
        "To begin an assessment, input a Request ID and find the patient by NHS number",
      caseReference: "Request ID",
      doYouHaveNhsNumber: "Do you have the NHS number?",
      manualInputWarningBanner: {
        title: "Proceed To Manual Input",
        confirmWithoutNHSNumberWarning:
          "Are you sure you want to continue without verifying the patient using their NHS number?",
        difficultyMatchingAssessmentWarning:
          "This will make it significantly harder to match the assessment to the correct patient when returned to the provider.",
        nhsNumberIrreversibleActionNote:
          "You will not be able to search for or verify the patient using their NHS number after this point.",
      },

      patientDetails: {
        title: "Patient Details",
      },
    },
    caseload: {
      beginSoadAssessment: "Begin SOAD Assessment",
      title: "Caseload",
      teamCaseload: "Team Caseload",
      soadCaseload: "SOAD Caseload",
      tabLabelUserCaseload: "Drafts",
      tabLabelArchive: "Archive",
      nhsNumber: "NHS number",
      searchLabel: "Search for patient in your caseload",
      searchLabelTeam: "Search for patient in your team",
      dateOfBirth: "Date of birth",
      myCaseload: "MY CASELOAD",
      guestUserBanner:
        "Archive history is limited to one month for guest users",
      noResultsSearchBanner:
        "You have no forms in your caseload. Create patient to start new form.",
      noResultsSearchBannerTeam:
        "No patients have been found in your team caseload with these details. Perform a new search with different data or search patients in your organisation.",
      noResultsWhenSearchBanner:
        "No patients have been found in your caseload with these details. Perform a new search with different data or create a new patient.",
      noResultsWhenSearchBannerTeam:
        "You have no forms in your team caseload. Search patient to start new form.",
      unavailable: {
        connectivity:
          "We could not reach Thalamos to load your caseload. Check your connection and try again.",
        server:
          "Your caseload could not be loaded just now. Try again in a moment.",
        session:
          "Your session was not recognised. Try again, and sign in again if that does not work.",
      },
      popupDialog: {
        popArchiveTitle: "Archiving in progress form",
        popupWarningText:
          "This item is still in progress. Are you sure you want to move this item to your archive? ",
      },
      table: {
        patientName: "Patient Name",
        dob: "DOB",
        nhsNumber: "NHS Number",
        form: "Form",
        summary: "Summary",
        claimed: "Claimed",
        actions: "Actions",
        requestId: "Request ID",
        caseStatus: "Case Status",
        soad: "SOAD",
        submittedTo: "Submitted To",
      },
    },
    formDraftPreviewPage: {
      popupDialog: {
        warningMessage:
          "Please confirm that you have read the form, are happy with the information, and are willing to proceed to the next step.",
        warningMessageAmends:
          "This amend is in effect from the time and date of the original signature. You do not need to re-sign the form.",
        formTitleSubtitleTextAmends: "Confirm amend",
        formTitleSubtitleText: "Confirm Completion",
      },
    },
    formDraftCompletionPage: {
      patientDetailsEdited: "Patient details edited",
      patientDetailsEditedBanner:
        "Patient details on this form have been edited. Please ensure the correct details are entered on this form before signing.",
      outcomeFormCompleteConfirm:
        "Are you sure you want to mark this outcome form as complete? This action cannot be undone.",
      outcomeFormPractitionerHeading:
        "Please confirm your details as the person completing this form",
      outcomeFormNotOwner:
        "This outcome form is currently being completed by another user. You have been redirected to the incident overview.",
    },
    favouriteRecipients: {
      title: "Favourite Recipients",
      subtitle:
        "Manage your favourite recipients for quick access when sharing forms. Favourites are organised by organisation.",
      emptyState:
        "You haven't added any favourite recipients yet. Use the star icon when sharing forms to add favourites.",
      remove: "Remove from favourites",
      removedSuccess: "Removed from favourites",
      backToProfile: "Back to Profile",
      activeOrganisation: "Current",
      countLabel_one: "{{count}} favourite",
      countLabel_other: "{{count}} favourites",
    },
    formDraftSignPage: {
      typeaheadLabel: "Enter email address of recipient",
      mhcProviderTypeaheadLabel: "Search for a healthcare provider",
      keepTypingHint: "Keep typing to search (minimum 3 characters)",
      formSubText: "Invite others to collaborate on this form",
      secondPractitionerNotified: {
        amendTitle:
          "Amend successfully confirmed and sent to the second medical practitioner",
        amendMessage:
          "The second registered medical practitioner has now been notified of the amend, there is no further action required from you.",
        redoTitle:
          "Redo successfully signed and sent to the second medical practitioner",
        redoMessage:
          "The second registered medical practitioner has now been notified that part 2 of the redo is theirs to complete, there is no further action required from you.",
      },
      favourites: {
        addFavourite: "Add to favourites",
        removeFavourite: "Remove from favourites",
        addFavouriteTooltip: "Add to favourites for quick access",
        removeFavouriteTooltip: "Remove from favourites",
        addToFavourites: "Add to favourites",
        removeFromFavourites: "Remove from favourites",
        selectTeamFirst:
          "Select a team below to add this person as a favourite",
        maxFavouritesReached:
          "You have reached the maximum number of favourites for this organisation. Please remove a favourite before adding a new one.",
      },
      medicalRecommendationsLabel:
        "Please select if one or more medical recommendations were not done on eMHA",
      dialog: {
        content:
          "Please confirm that you have read the form, are happy with the information, and are willing to proceed to the next step.",
      },
      supportingDocuments: {
        redoBasisWarning: {
          medicalRecommendations:
            "A Medical Recommendation previously selected for this form has been redone or is currently being redone. To continue, reselect a valid combination of Medical Recommendations as the basis for this form.",
          application:
            "An Application previously selected as the basis of this form has been redone or is currently being redone. To continue, you must select a valid Application as the basis for this form.",
        },
        patientMergeWarningMessage:
          "You are about to select a form that has been merged from another patient record, patient name may not be the same. Please check the details carefully before signing. ",
        validationSuccess: {
          messageTitle: "Successful selection",
          messageInfo:
            "It is still your obligation to scrutinise the medical recommendation(s) and their validity.",
        },
        validationError: {
          messageTitle: "Unsuccessful selection",
        },
      },
    },
    formPage: {
      unfinaliseAdmission: {
        title: "Undo Legal Scrutiny",
        warning:
          "You are about to undo legal scrutiny for this work item. Please provide your reasons for this action below. This action will not affect the patient’s current MHA Status.",
        reasonLabel:
          "Please select a reason why legal scrutiny needs to be undone",
        additionalInfoLabel: "Please provide additional information",
      },
      patientDetailsOutdatedAdmissions:
        "The patient details have been updated in Thalamos since the first admission form or form part was signed. This may invalidate the admission - please check the details carefully before signing.",
      patientDetailsOutdated:
        "The patient details have been updated in Thalamos since this form was first signed. This may invalidate the form - please check the details carefully before signing",
      formTitle: "Form progress",
      startForm: "Start Form",
      requestForm: "Request Form",
      application: "Application",
      recordDetentionFormTitle: "Start Record of Detention Form",
      section4ActiveBanner:
        "A patient admitted under section 4 may be detained only for a maximum of 72 hours unless a second medical recommendation is provided and part 2 of form H3 is completed. The second medical recommendation must be a single section 2 form. Continue part 2 to record the time and date the second medical recommendation was received.",
      section4ExpiredBannerTitle: "Section 4 expired ",
      section4ExpiredBannerBody:
        "A patient admitted under section 4 may be detained only for a maximum of 72 hours unless a second medical recommendation is provided and part 2 of Form H3 is completed. Part 2 has not been completed within the 72 hour period. Everyone involved in the assessment should discuss how to proceed.",
      unassigned: "Unassigned",
      noSignaturesYet: "None Yet",
      previewPdf: "preview PDF",
      viewPdf: "view PDF",
      status: "Status",
      preview: "Preview",
      signatories: "Signatories",
      part: "Part",
      userName: "User Name",
      teamName: "Team Name",
      organisationName: "Organisation Name",
      signatureDate: "Signature Date",
      typeaheadLabel: "Add new collaborator",
      participants: "Participants",
      notificationStatusNotViewed: "Not Viewed",
      notificationStatusViewed: "Viewed",
      linkedForms: "Linked Forms",
      formType: "Linked Form Type",
      nameDoesNotMatchPreviousFormPart:
        "Name entered on form is different to the previous part of this form you signed",
      addressDoesNotMatchPreviousFormPart:
        "Address entered on form is different to the previous part of this form you signed",
      postalCodeDoesNotMatchPreviousFormPart:
        "Postcode entered on form is different to the previous part of this form you signed",
      nameDoesNotMatchProfile:
        "Name entered on form is different to the name saved in your profile",
      addressDoesNotMatchProfile:
        "Address entered on form is different to the address saved in your profile",
      postalCodeDoesNotMatchProfile:
        "Postcode entered on form is different to the postcode saved in your profile",
      bannerInfoArchiveAdmission:
        "This admission is in your team's archive. It has been confirmed that a medical recommendation will not be made for this patient. Please open the activity log to view assessment details. Unarchive to proceed working on the possible admission for this patient if circumstances have changed.",
      bannerInfoArchiveAdmissionNoApp:
        "It has been confirmed that an application will not be made for this patient. Please open the activity log to view assessment details. You can click reopen to proceed working on the possible admission for this patient if circumstances have changed.",
      bannerInfoArchiveAdmissionNoAppNonAmhp:
        "This admission is in your team's archive. It has been confirmed that an application will not be made for this patient. Please open the activity log to view assessment details. If you feel you should proceed working on the possible admission for this patient please contact one of the approved mental health professionals outside of Thalamos and ask them to reopen the admission.",
      bannerInfoArchive:
        "This form is in your team's archive. Unarchive it to work on it",
      quickItemTextSection:
        "Send forms to another team and request the record of detention in hospital to be completed",
      quickItemTextShare: "Send form(s) to another team to work on it",
      bannerWarningNextPart:
        "This assignee cannot work on the next part of this form. This might be the right thing to do (e.g. if you want them to help you co-ordinate the form) but please double check you've assigned to the right person/team",
      checkBoxLabelArchive:
        "Archive this form from your caseload when sharing?",
      formTitleLinked: "This form will be linked to the current form",
      checkBoxLabelStartWork: "Start work on this form yourself",
      finaliseForm: "Scrutinise this form",
      typographyTextSent: "Sent",
      headingTextReceiver: "Receiver",
      teamNameBannerText: "You are working for",
      admissionStandardTitle: "Start Medical Recommendation Form",
      applicationFormTitle: "Application Form",
      autoSaveStatusDraft: {
        saveInProgress: "Saving {{ type }}",
        saveSuccess: "{{ type }} Saved",
        saveError: "Error saving {{ type }}",
      },
      autoSaveStatusProfile: {
        saveInProgress: "Saving profile",
        saveSuccess: "Profile Saved",
        saveError: "Error saving profile",
      },
      formRowCategory: {
        recordofDetentionTitle: "Record of Detention",
        h3Subtitle: "Form H3 -",
        medicalRecommendationTitle: "Medical Recommendation",
        multiSections: "Section 2, 3 and 4",
        requestMedicalRecommendationTitle: "Request Medical Recommendation",
        requestApplicationTitle: "Request Application",
        inviteDoctorTitle: "Invite Doctor for MHA Assessment",
        inviteDoctorSubtitle:
          "Invite specific doctor or get help finding an independent Section 12 doctor.",
        inviteDoctor: "Invite Doctor",
      },
      participantsList: {
        popupDialog: {
          title: "Invite declined ",
          titleWithdrawn: "Invitation withdrawn",
          admissionTitleNoMedRec: "Medical recommendation not made",
          admissionTitleNoApp: "Application not made",
          admissionReadOnlyContentTitleMedRec:
            "Reason why the medical recommendation was not made",
          admissionReadOnlyContentTitleNoApp:
            "Reason why the application was not made",
          readOnlyContentTitle: "Reason why the invite was declined",
          readOnlyIncorrectDetailsTitle: "Incorrect Details",
          readOnlyAdditionalInfoTitle: "Additional Information",
        },
      },
      recordReasonDialog: {
        mandatoryDescriptionTitle: "Please include additional information",
        optionalDescriptionTitle:
          "Please include additional information if required",
      },
    },
    organisationHomePage: {
      organisation: "Organisation",
    },
    organisationCreateEdit: {
      changeDataControllerConfirmationDialog: {
        title: "Are you sure you want to change the Data Controller?",
        heading:
          "This will change what forms the user has access to.\nAny change for data controller requires a CCM ticket, approved by two individuals.\nPlease submit who approved this change.",
      },
    },
    patientHome: {
      title: "Patient",
      newForm: "New Form",
      admissionFormTitle: "Admission Forms",
      section: "Section",
      connectingToExternalSystem: "Connecting to external system..",
      returnToPatientHome:
        "Not loading? Click here to continue to the patient home page without synchronising demographics.",
      viewLess: "View less",
      viewMore: "View more",
      launchForm: {
        confirmAction: "Confirm Action",
        startNew: "Start New Form",
        requestFormCompletion: "Invite another user or team to start new form",
        whatDoYouWantToDo: "What do you want to do?",
        selectForm: "Select Form",
        searchTeamUser: "Search for Team/User",
        inviteUser: "Invite",
        startForm: "Start Form",
        close: "Close",
        admissionProgressTitle: "MHA Assessment already in progress",
        admissionWarningBannerTitle:
          "Section papers are already in progress for this patient. Continue to the existing MHA assessment to access and complete forms.",
        admissionInProgressCard: {
          title: "MHA Assessment",
          noSignedForms: "No signed forms yet",
        },
        startNewAssessment:
          "Start a new assessment (do not continue to an in progress assessment)",
        popupDialogTitle: "Form already in progress",
        formUnavailable: "This form is not available to start here.",
        formProgressWarningMessage:
          "has already been started for this patient. Continue working on the form below or start a new one.",
        radioSelectorWarningMessage:
          "Please select a form and click continue or start new form",
        inviteSuccessfullySentLabel: "Invite successfully sent",
        inviteSuccessfullySentMessage: "Invite successfully sent",
        admissionValidityInstructions:
          "The section 2 and 3 application is valid for 14 days from the last time the doctor examined the patient. The emergency application is valid for 24 hours from the last time the doctor examined the patient or when the application was made. Please make sure to check the validity of the admission forms before proceeding. If you are unsure, please speak to your mental health act team.",
        detentionApplicationSelectionPrompt:
          "Please select the application made for purpose of the detention",
      },
    },
    s12PaymentRequestDialog: {
      localAuthoritySelectionLabel:
        "Local Authority responsible for this payment",
      arrangingLocalAuthorityLabel:
        "Local Authority which arranged this assessment",
    },
    patientEditCreate: {
      createError: "An error occurred when creating the patient",
      createSuccess: "Patient created successfully",
      updateError: "Some kind of error occurred when updating the patient",
      updateSuccess: "Patient updated successfully",
      updateSuccessDialogMessage:
        "Patient details have successfully been updated.",
      editPatientInformation:
        "You are about to edit the patient information for {{givenName}} {{familyName}}. Information should only be edited if the details are inaccurate. If you need to do a new form for a different patient <a href='{{patientSearchRoute}}'>click here</a>. Patient information will not be updated on any signed forms or form parts.",
      editPatientConfirmation:
        "You are about to edit the patient information for {{givenName}} {{familyName}}. Please ensure you have entered the correct details. Patient information will not be updated on any signed forms or form parts.",
      confirmBanner:
        "You are significantly changing patient details. To create a form for a different patient, you must <a href='{{patientSearchRoute}}'>click here</a>. To continue, please confirm you understand by typing CONFIRM below.",
      gpPractice: {
        title: "Registered GP Practice",
        info: "This information is managed in NHS Spine systems and cannot be updated in eMHA.",
        odsCode: "ODS Code",
        practiceName: "Practice Name",
        practiceAddress: "Address",
        telephone: "Telephone",
        call: "Call practice",
        loading: "Loading...",
      },
      officialGender: {
        title: "Official Gender (Spine)",
        info: "This is the gender that is recorded on official documents, for example a birth certificate or passport. For some people, the gender held in PDS (NHS Spine Systems) might not match the gender they identify with. This cannot be edited in eMHA",
        options: {
          male: "Male",
          female: "Female",
          other: "Other",
          unknown: "Unknown",
        },
      },
      rioEthnicity: {
        title: "Ethnicity (Rio)",
        info: "This is the ethnicity recorded for the patient in their Rio record. It is pulled automatically from Rio and cannot be edited in eMHA.",
        source: "Last updated from {{rioInstanceName}} on {{lastPulledAt}}",
        unknownInstance: "Rio",
      },
    },
    patientSearch: {
      title: "Search Patient",
      mergePatientTitle:
        "Search for patient you want to merge this record into ",
      usePhoneticMatch: "Enable phonetic/soundex match",
      searchByNhsNumber: "Search by NHS number",
      searchByDemographics: "Search by Demographics",
      checkNhsNumberLabel: "I have the NHS Number",
      textFieldNhsLabel: "NHS Number",
      textFieldNhsInfo: "An NHS Number is a 10-digit number",
      phoneticTooltipText:
        "These are results which are different to what you have searched for but are similar or sound the same.",
      tabs: {
        labelOne: "Search eMHA",
        labelTwo: "Search Rio",
      },
      givenName: "Enter patient's forename",
      givenNameRequired: "Enter patient's forename (required)",
      familyName: "Enter patient's surname (required)",
      postalCode: "Enter patient's postcode",
      dateOfBirth: "Enter patient's date of birth",
      radioDob: "Do you have patient's date of birth?",
      radioDobRequired: "Do you have patient's date of birth? (required)",
      dropdownDateRangeLabel: "Enter approximate age range",
      patientCard: {
        nhsNumber: "NHS Number",
        dateOfBirth: "DOB",
        formControlLabel: "Select",
      },
      dateRange: {
        underAge18: "Under age 18",
        age18To39: "Age 18 to 39",
        age40To59: "Age 40 to 59",
        age60To79: "Age 60 to 79",
        overAge80: "Over age 80",
        unknown: "UNKNOWN",
      },
      searchReasons: {
        popupDialogTitle: "Provide reason for access",
        popupDialogBody:
          "You must only access records of patients with a legitimate care relationship, please confirm the patient details and provide a valid reason for accessing this record. This access will be audited.",
        dropdownLabel: "Select reason",
        errorStates: {
          noPatientSelected: "No patient selected",
          selectAReason: "Please select a reason",
          provideReasonDescription: "Please provide a reason description",
        },
        otherReasonTextfieldLabel: "Reason description",
        searchReasonsValues: {
          emergencyOnCall: "Emergency/On-Call",
          adminAddDetails: "Admin/Add Details",
          referral: "Referral",
          newEpisodeOfCare: "New Episode of Care/New Form Context",
          auditInvestigationComplaint: "Audit/Investigation/Complaint",
          technicalSupport: "Technical Support",
          other: "Other",
        },
      },
      patientSearchResult: {
        title: "Search Result",
      },
      patientCreate: {
        title: "Create Patient",
        subtitle: "Create patient with demographics",
        familyNameAndAddress: {
          nameLabel: "Enter patient's surname*",
          addressLabel: "Enter patient's home address*",
          soadAddressLabel: "Enter patient's address*",
        },
        givenName: "Enter patient's forename*",
        isDOBlabel: "Do you have patient's date of birth*",
        dateOfBirth: "Enter patient's date of birth",
        deathNotificationStatusLabel: "Death Notification Status",
        deathNotificationStatusOptions: {
          formal: "Formal",
          informal: "Informal",
          deathStatusRemoved: "Death Status Removed",
        },
      },
      patientSearchResultBanner: {
        uniqueMatch: {
          title: "Patient match",
          body1:
            "A patient has been found with the details above.{{continueInstruction}} ",
          body2:
            "If this is not the correct patient, clear the search and perform a new search with different data{{alternativeInstruction}}",
          patientMergeBody2:
            "If this is not the correct patient, clear the search and perform a new search with different data.",
        },
        multipleMatches: {
          title: "Multiple matches",
          body1:
            "Multiple patients have been found with the details above. Select the correct patient and click continue. \n",
          body2:
            "If this is not the correct patient, clear the search and perform a new search with different date or create a new patient.",
          patientMergeBody2:
            "If this is not the correct patient, clear the search and perform a new search with different date.",
        },
        noMatches: {
          title: "No match",
          body: "No patients have been found with the details above. Clear the search and perform a new search with different data{{alternativeInstruction}}",
          patientMergeBody:
            "No patients have been found with the details above. Clear the search and perform a new search with different data.",
        },
        unknown: {
          title: "Unknown error",
          body: "Something went wrong searching. The Thalamos support team have been informed. Please try again later.",
        },
      },
      stepper: {
        searchPatient: "Search Patient",
        selectPatient: "Select Patient",
        newForm: "New Form",
        merge: "Merge Patient",
      },
    },
    patientSearchV2: {
      banner: {
        createWarning:
          "Please search for existing patients to avoid duplication, you can create a new patient record once you have searched. ",
      },
    },
    userProfile: {
      title: "Profile",
      subtitle: "View and manage your profile details",
      accreditationsSectionTitle: "Professions and registrations",
      noTeamsOrOrganisationsLabel: "No teams or organisations",
      formAccreditationsLabel: "Accreditations",
      manageFavouriteRecipients: "Manage Favourite Recipients",
      profRegDropdown: {
        label:
          "Please select the professional bodies you are registered with (if any)",
        options: {
          gmc: "General Medical Council (GMC)",
          nmc: "Nursing and Midwifery Council (PIN)",
          hcpc: "Health & care professions council (HCPC)",
          swe: "Social Work England (SWE)",
        },
      },
      defaultMembership: "Default membership",
      mhApprovalsDropdown: {
        label: "Please select the roles you are approved for (if any)",
        options: {
          amhp: "Approved Mental Health Professional (AMHP)",
          ac: "Approved Clinician",
          s12: "Section 12 Approved",
        },
        info: {
          ac: "Approved Clinician Status is a specific approval under the Mental Health Act. You must have completed the relevant training and be approved by your regional approvals panel in order to be an Approved Clinician. If you are unsure whether you are an Approved Clinician, check with your mental health law team.",
          s12: "Section 12(2) is a specific approval under the Mental Health Act. You must have completed the relevant training and be approved by your regional approvals panel in order to be Section 12(2) approved. If you are unsure whether you are Section 12(2) approved, check with your mental health law team.",
        },
      },
      section12Approved: "Section 12 Approved",
      approvedClinician: "Are you an approved clinician?",
      approvedClinicianExpiryDate:
        "Enter approved clinician approval expiry date",
      localAuthorityFormLabel: "Enter the local authority you are approved by",
      gmcNumberLabel: "Enter General Medical Council (GMC) registration number",
      pinNumberLabel: "Enter Nursing and Midwifery Council (NMC) PIN number",
      hcpcNumberLabel:
        "Enter Health & Care Professions Council (HCPC) registration number",
      socialWorkerNumberLabel:
        "Enter Social Work England (SWE) registration number",
      workAddress: "Work address",
      phone: {
        title: "Phone Number for Doctor Contact",
        description:
          "Provide a phone number where independent doctors can contact you about assessment requests",
        label: "Phone Number",
        placeholder: "07700 900000",
        helperText:
          "Enter your UK mobile number (e.g., 07700 900000 or +447700900000)",
        invalidFormat: "Please enter a valid UK phone number",
        consentLabel:
          "I consent to independent doctors contacting me at this phone number regarding assessment requests",
        saveButton: "Save Phone Number",
        savingButton: "Saving...",
        changeButton: "Change Phone Number",
        removeButton: "Remove Phone Number",
        savedSuccess: "Phone number saved",
        savedError: "Failed to save phone number",
        removedSuccess: "Phone number removed",
        removedError: "Failed to remove phone number",
      },
    },
    onBoarding: {
      formTitle: "Confirm Details",
      subtitle: "Enter professional details",
      dropdown: {
        label: "Select occupation(s)",
      },
      confirmOccupation: {
        formTitle: "Confirm occupation",
        gmcNumber: "GMC Number",
        isSection12Approved: "Are you Section 12 approved",
        section12ExpiryDate: "Enter section 12 approval expiry date",
        isApprovedClinician: "Are you an approved clinician",
        approvedClinicianExpiryDate: "Enter approved clinician expiry date",
      },
    },
    formDraftFlow: {
      standardFlow: {
        stepOne: "Complete form",
        stepTwo: "Sign form",
        stepThree: "Send form",
      },
      draftUnavailable:
        "This draft isn't available. It may have been signed or discarded.",
    },
    notifications: {
      patientLabel: "Patient",
      title: "Notifications",
      noNotifications: "No Notifications Yet",
      soadAssessment: "SOAD Assessment",
      section136: "Section 136",
      mhaAssessment: "MHA Assessment",
      teamSelectionDialog: {
        title: "Please select the team this form should appear in",
        bannerInfo:
          "This team should be associated with your Emergency Department/Place of Safety.",
        teamLabel: "Team",
        close: "Close",
        continue: "Continue",
      },
    },
    dashboard: {
      popupDialog: {
        warningMessage:
          "You are about to download personal identifiable data. Please explain why and enter the details of your manager who will be notified once you confirm. Please do not include any patient identifiable data in your reasoning. ",
      },
    },
    admissionFormPage: {
      bannerTitle: "Amend period expired",
      amendPeriodExpired72Hours:
        "The 72-hour rectification period has now expired and the form cannot be amended under Section 15, if there is an identified error after this time period; please discuss with your Mental Health Act administration team.",
      amendPeriodExpired14Day:
        "The 14-day rectification period has now expired and the form cannot be amended under Section 15, if there is an identified error after this time period; please discuss with your Mental Health Act administration team.",
      scrutiniseAdmissionBannerTitle:
        "Mark administrative scrutiny of this admission complete",
    },
    redoDialog: {
      start: {
        intro: "You are about to start a redo on {{ formDescription }}.",
        originalReplaced:
          "Once you sign the new form, the original is replaced.",
        considerDeMinimis:
          "If the change you are intending to make would not invalidate the legality of the patient's detention, consider making a de minimis (minor change) amendment following the signing of the H3.",
      },
      request: {
        intro:
          "You are about to request that {{ formDescription }} is redone. The request is sent to the person who completed the form.",
        originalReplaced:
          "When the redo is completed the original form is replaced with a new signed form.",
        considerDeMinimis:
          "If the change you are requesting would not invalidate the legality of the patient's detention, consider making a de minimis (minor change) amendment following the signing of the H3.",
      },
      contactedAuthorConfirmation:
        "I have contacted the author of the form directly to inform them of this redo request",
    },
    formUpload: {
      linkingFormsWarningBannerTitle:
        "You are about to select a from that has been uploaded, please make sure the medical recommendations are made by two different doctors.",
      firstPractitionerTitle: "First practitioner",
      secondPractitionerTitle: "Second practitioner",
      certifyThatCheck:
        "I certify that this is a true and exact reproduction of the original form, is of sufficient quality to allow readability of all information and is relevant to the care for this patient",
      confirmLabel: "Please check to confirm",
      confirmLabelAriaLabel: "checkbox for confirmation",
      previewLabel: "Preview Form Label",
      successMessageTitle: "Form uploaded successfully",
      successMessage: "Form has successfully been uploaded",
      warningBannerMessage:
        "Are you uploading a word document? Please save the document as a PDF before you upload, word files cannot be uploaded to the system (open the document, click save as and select PDF or download as PDF).",
      mixedFileDetectedErrorMessage:
        "Your upload contains a mix of file types. Please upload files of the same type for processing. Ensure all files are either PDFs or images.",
      uploadLabelSubtext:
        "You can upload exactly one PDF file or multiple images (JPG/PNG/GIF/WEBP format). Maximum file size is 5MB.",
      formDetailsLabel: "Confirm form details",
      uploadingLabel: "Uploading...",
      selectedFormLabel: "Select the form you are uploading",
    },
    patientStateDisplay: {
      whatIsMHAStatus: "What is MHA status?",
      mhaStatusExplained:
        "The MHA status captures the current MHA status of the patient and may automatically update when a new form is completed. The status refers to in-hospital MHA detentions, CTOs, discharges and s136 detentions.",
      informationFrom: "Where is this information from?",
      informationFromExplained:
        "The status, expiry, renewals/extensions and organisation displayed are either automatically populated from a Thalamos form, on eMHA or by the police, or have been manually input by an administrator. They reflect the most recent change on the eMHA system. To see more details, hover over the information icons in the banner. If non-Thalamos forms have been used, or if the patient has been moved without manual updates, the status may not be accurate.",
      contactLegalTeam:
        "Please contact your local MHA Legal Team if the status is inaccurate or not reflecting the care for this patient.",
    },
    policeIncidentFormPage: {
      title_s136: "Section 136",
      title_mca: "Mental Capacity Act",
      placeOfSafety: "Place of safety",
      synchroniseLoadingMessage: "Synchronising with police system...",
      failedPdfDownload: "Failed to download PDF",
      failedSynchroniseIncident: "Failed to synchronise incident",
      failedToFetchIncidentId: "Failed to load incident details",
      incidentNotFound:
        "The incident associated with this form context could not be found.",
      failedCreateForm: "Failed to create form",
      ongoingPolicePresenceRequestLog: "Ongoing police presence request log",
      update: "Update",
      lastUpdated: "Last updated",
      viewDetails: "View Details",
      continue: "Continue",
      subtitle_s136: "Section 136",
      subtitle_mca: "Mental Capacity Act",
      sectionTitleForms: "Forms",
      sectionTitleInProgress: "In-progress forms ({{ count }})",
      sectionTitleCompleted: "Completed forms ({{ count }})",
      downloadAll: "Download all",
      uploadAllToRio: "Upload all to Rio",
      itemName_s136: "136 incident",
      itemName_mca: "MCA incident",
      markScrutinyComplete:
        "Mark administrative scrutiny for this {{ itemName }} complete",
      inviteToExtendS136:
        "Invite the registered medical practitioner responsible for the examination for this patient to extend the Section 136",
      accessedChipLabel: "Accessed",
      formAccessNotice: {
        invitationMessage:
          "Officer {{ name }} has invited you to access a section 136 form for the above patient. This information is provided by the police and is subject to change as the incident evolves. Please review the entire form at the point of progressing. <strong>To download the form and share onwards, you must click ‘continue to download’ and inform police presence.</strong>",
      },
      created: "Created:",

      formTitles: {
        policeIncident_s136: "Section 136 Police Form",
        policeIncident_mca: "Mental Capacity Act Police Form",
        requestPolicePresence: "Request ongoing police presence?",
        ongoingPolicePresence:
          "Your input will be shared with the responsible officer to inform decision making.",
        sectionOutcome: "Section 136 Outcome Form",
        s136Extension: "Section 136 Extension Form",
      },
      notPoliceRequiredDialog: {
        title: "Confirm no police presence required",
        response: "Your response will be shared back with the officer",
        policeConfirm:
          "Please note, if circumstances change you must speak directly with police to confirm whether they are able to remain",
      },
      ongoingPolicePresenceDialog: {
        hasPolicePresenceBeenRequested: "Has police presence been requested?",
        hasPolicePresenceBeenRequestedYes: "Yes",
        hasPolicePresenceBeenRequestedNo: "No",
        hasPolicePresenceBeenRequestedAlreadyLeft: "Already left",
        title: "Request ongoing police presence at {{ name }}",
        warningBannerBody1:
          "Ongoing police presence is not guaranteed but subject to officers agreement and local policy and procedure.",
        warningBannerBody2:
          "Please speak to the officer present if there is a change in circumstance",
        dropdown: {
          placeholder: "Select a reason",
          title: "Please explain why police presence is needed",
          errors: {
            reasonError: "Please select a reason",
            otherReasonError: "Please provide more information",
            otherReasonTooLong:
              "Text limit reached, please reduce the number of characters.",
          },
          options: {
            immediateRiskToLifeOrOfSeriousHarm:
              "To protect people from an immediate risk to life, or being subject to/at risk of serious harm",
            notEnoughStaff: "Not enough staff",
            transfer:
              "Person to be transferred to another place of safety for assessment",
            other: "Other",
          },
          otherReasonLabel: "Please provide more information",
        },
        provideFurtherExplanation:
          "Please provide further explanation behind the reasons selected, including any risk assessments completed",
        recordPolicePresence: {
          errors: {
            officer:
              "Please add the name of the officer involved in this Section 136 incident.",
            reason: "Please add further details",
          },
          officerLabel: "Name of the Officer involved",
          reasonLabel: "Please add any additional details as needed (optional)",
          officerPlaceholder: "Please input first and last name",
        },
      },
      toastNotifications: {
        policePresenceRequested: "Police presence requested",
        policePresenceNotRequested: "Police presence not required",
        requestPolicePresenceFailed: "Failed to request police presence",
        policePresenceInformed: "Police presence input recorded successfully.",
      },
      statusChips: {
        requested: {
          label: "Requested",
          body: "Pending response from police",
        },
        accepted: {
          label: "Accepted",
          body: "Police confirmed ongoing police presence",
        },
        rejected: {
          label: "Rejected",
          body: "Police declined ongoing police presence",
        },
        decisionNeeded: {
          label: "Decision needed",
        },
        notRequested: {
          label: "Not required",
          body: "police presence not required",
        },
      },
      outcomeForm: {
        yourDraft: "Your draft",
        lastWorkedOnBy: "Last worked on by {{ name }}",
        autoClosed: "Auto-closed",
        failedCreate: "Failed to create outcome form",
        failedClaim: "Failed to claim outcome form",
        claimedFrom: "Claimed outcome form from {{ name }}",
        claimConfirmMessage:
          "This form for {{ posName }} was last worked on by {{ name }}. Only one person can work on the form at a time. If you continue, you will take over editing.",
        claimPosConfirmCheckbox: "I confirm I can represent {{ posName }}",
        addForPos: "Add an outcome form for a place of safety",
        addOutcomeForm: "Add outcome form",
        selectPosTitle: "Select place of safety",
        selectPosSubtitle:
          "Choose the place of safety this outcome form relates to",
        posMode: {
          incident: "From incident",
          fullList: "Full list",
          manual: "Enter manually",
          searchList: "Search list",
        },
        activePos: "Active",
        selectIncidentPos: "Select a place of safety from this incident",
        selectCuratedPos: "Select a place of safety",
        searchPos: "Search for a place of safety",
        posGroup: {
          onIncident: "Already registered on this incident",
          allLocations: "All locations",
        },
        posChip: {
          onIncident: "Already registered on this incident",
          formExists: "Form exists",
        },
        manualPosName: "Place of safety name",
        manualPosAddress: "Address",
        manualPosPostalCode: "Postal code",
        startOutcomeForm: "Start outcome form",
        viewPdf: "View PDF",
      },
    },
    incidentPreviewPage: {
      formControlLabel: "Is this your place of safety for this incident?",
      isCurrentPlaceOfSafetyLabel:
        "Is the patient currently at this place of safety?",
      incidentPreviewTitle: "Incident Preview",
      selectPlaceOfSafetyMessage: "Please select a place of safety",
      confirmDetails: "Please review the details below",
      dropdownPosTitle: "Select another Place of Safety",
      dropdownItemPlaceholder: "Select a place",
      incidentNotFoundMessage:
        "Incident ID not found. Please try to login through share code/email link again",
      incidentOfficer:
        "Officer {{name}} has shared a Section 136 Form with you for the following patient",
      mcaIncidentBannerBody:
        "Officer {{officerName}} has invited you to access a Mental Capacity Act form for the above patient. This information is provided by the police and is subject to change as the incident evolves.",
      receivedInError: "Received in Error",
      failedRejectIncident: "Failed to reject incident",
      incidentRejected: "Incident rejected",
      incidentAccepted: "Incident accepted",
      failedAcceptIncident: "Failed to accept incident",
      selectPlace: "Select a place",
      manuallyEnter: "Manually enter",
      warningBannerText:
        "Please speak to the officer present if you believe the place of safety needs to be changed",
      name: "Name",
      dateOfBirth: "Date of birth",
      address: "Address",
      postcode: "Postcode",
      trust: "Trust",
      selectTrust: "Please select a trust",
      trustAutoSelected:
        "The trust has been automatically selected based on the place of safety",
      confirmAddress: "I confirm this address is correct",
      selectTrustTitle:
        "Please select the trust associated with this place of safety",
      trustNotListedReconciliationWarning:
        "Patient reconciliation may not be available without a known trust.",
    },
    posManagement: {
      table: {
        odsCode: "ODS Code",
        name: "Name",
        address: "Address",
        postcode: "Postcode",
        posType: "POS Type",
        trust: "Trust",
        features: "Features",
        s136Extension: "S136 Extension",
        outcomeForm: "Outcome Form",
        actions: "Actions",
        delete: "Delete",
        edit: "Edit",
      },
      deleteModal: {
        title: "Are you sure you want to delete this place of safety?",
        content: "You are about to delete the following place of safety:",
        warning:
          "These details will be permanently deleted and will no longer be available to officers completing Section 136 forms on MH Forms.",
        confirm: "PERMANENTLY DELETE RECORD",
        successMessage: "{{posName}} POS details deleted",
        errorMessage: "Failed to delete place of safety",
      },
    },
    mentalHealthCareProviderManagement: {
      filterPlaceholder: "Filter providers...",
      add: "Add Provider",
      tableLabel: "Mental health care providers table",
      empty: "No mental health care providers found",
      loadError: "Failed to load mental health care providers",
      table: {
        name: "Name",
        email: "Email",
        trust: "Trust",
        actions: "Actions",
        edit: "Edit",
        delete: "Delete",
      },
      createEditModal: {
        createTitle: "Create Mental Health Care Provider",
        editTitle: "Edit Mental Health Care Provider",
        createSubcopy:
          "Fill out the details to create a new mental health care provider.",
        editSubcopy: "Update the mental health care provider details below.",
        name: "Name",
        email: "Email",
        trust: "Trust",
        cancel: "Cancel",
        save: "Save",
        nameRequired: "Name is required",
        emailRequired: "Email is required",
        emailInvalid: "Enter a valid email address",
        trustRequired: "Select a trust from the list",
        createSuccessMessage: '"{{providerName}}" created successfully.',
        editSuccessMessage: "Provider updated successfully.",
        createErrorMessage: "Failed to create mental health care provider.",
        editErrorMessage: "Failed to update mental health care provider.",
      },
      deleteModal: {
        title: "Delete Mental Health Care Provider",
        content: "Are you sure you want to delete this provider?",
        warning:
          "The provider will no longer be available to assign new SOAD assessments to, and cannot be restored. Assessments already sent to them keep their record of it.",
        cancel: "Cancel",
        confirm: "Delete",
        successMessage: 'Provider "{{providerName}}" deleted successfully.',
        errorMessage: "Failed to delete mental health care provider.",
      },
    },
    posCreate: {
      title: "You are adding a new POS record",
      subcopy:
        "Please make sure you are not creating a duplicate record. If a similar record already exists, you can edit the record to make sure its correct.",
      name: "Place of Safety Name",
      address: "Address",
      postcode: "Postcode",
      type: "Place of Safety Type (optional)",
      odsCode: "ODS Code (optional)",
      successMessage: "{{ posName }} Added",
      errorMessage: "Failed to create Place of Safety",
      email: "Place of Safety shared email address (Optional)",
      addEmail: "Add +",
      addedEmails: "Added email addresses",
      emhaOrgId: "Linked eMHA Organisation ID (Optional)",
      invalidEmail: "Please enter a valid email address.",
      orgIdExplainer:
        "Can be found by going to organisation list, clicking edit on the relevant organisation and copying the ID from the URL",
      featureFlags: "Feature Flags",
      s136ExtensionFormEnabledLabel: "Enable S136 Extension Form",
      outcomeFormEnabledLabel: "Enable Outcome Form",
    },
    posEdit: {
      title: "You are changing official Place of Safety details",
      subcopy:
        "Editing a Place of Safety will change information for Section 136 forms that officers can complete and should only be done with appropriate sign off.",
      successMessage: "Place of safety details changed",
      errorMessage: "Failed to change Place of Safety details",
    },
    demographicsManager: {
      trustFilterTooltip:
        "A patient's trust is taken from their latest MHA status. Patients won't appear when filtering by trust if they have no MHA status, their latest status has no associated trust, or their latest trust is outside the current organisation.",
    },
  },
};
